package twist.mena.sports.admin.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import twist.mena.sports.admin.api.AdminAdsApi
import twist.mena.sports.admin.components.*
import twist.mena.sports.admin.state.AuthState
import twist.mena.sports.admin.state.UiState
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.OffsetPage
import twist.mena.sports.shared.dto.ads.AdResponse

@Composable
fun AdsScreen(api: AdminAdsApi) {
    val colors = AppTheme.colors
    var state by remember { mutableStateOf<UiState<OffsetPage<AdResponse>>>(UiState.Loading) }
    var search by remember { mutableStateOf("") }
    var offset by remember { mutableStateOf(0) }
    var showCreateDialog by remember { mutableStateOf(false) }
    var editingAd by remember { mutableStateOf<AdResponse?>(null) }
    val scope = rememberCoroutineScope()
    val isEditor = AuthState.getRole()?.let {
        it in listOf("scora_editor", "scora_admin", "scora_super_admin")
    } ?: false

    fun loadAds() {
        scope.launch {
            state = UiState.Loading
            try {
                val page = api.listAds(search.ifBlank { null }, offset, 50)
                state = UiState.Success(page)
            } catch (e: Exception) {
                state = UiState.Error("Failed to load ads")
            }
        }
    }

    LaunchedEffect(search, offset) { loadAds() }

    // Top controls
    Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(Spacing.md); marginBottom(Spacing.lg); flexWrap(FlexWrap.Wrap) } }) {
        SearchBar(placeholderText = "Search ads...") { query ->
            search = query
            offset = 0
        }

        if (isEditor) {
            Button({
                onClick { showCreateDialog = true }
                style {
                    padding(Spacing.sm, Spacing.md)
                    primaryButtonStyle(colors)
                    fontSize(Typography.sm)
                    borderRadius(BorderRadius.md)
                }
            }) { Text("Add Ad") }
        }
    }

    when (val s = state) {
        is UiState.Loading -> P({ style { color(Color(colors.textMuted)) } }) { Text("Loading...") }
        is UiState.Error -> P({ style { color(Color(colors.danger)) } }) { Text(s.message) }
        is UiState.Success -> {
            val page = s.data
            val columns = listOf(
                Column<AdResponse>("Type") { ad ->
                    Span({
                        style {
                            padding(2.px, 8.px)
                            borderRadius(BorderRadius.sm)
                            fontSize(Typography.xs)
                            backgroundColor(Color(if (ad.adType == "BANNER") colors.primary else colors.warning))
                            color(Color("#ffffff"))
                        }
                    }) { Text(ad.adType) }
                },
                Column("Location") { ad ->
                    Text(ad.adLocation)
                },
                Column("iOS Ad ID") { ad ->
                    Span({ style { fontSize(Typography.xs); property("word-break", "break-all") } }) {
                        Text(ad.iosAdId)
                    }
                },
                Column("Android Ad ID") { ad ->
                    Span({ style { fontSize(Typography.xs); property("word-break", "break-all") } }) {
                        Text(ad.androidAdId)
                    }
                },
                Column("Status") { ad ->
                    if (!ad.show) {
                        Span({ style { color(Color(colors.textMuted)); fontSize(Typography.sm) } }) { Text("Hidden") }
                    } else {
                        Span({ style { color(Color(colors.success)); fontSize(Typography.sm) } }) { Text("Visible") }
                    }
                },
                Column("Actions") { ad ->
                    if (!isEditor) return@Column
                    Div({ style { display(DisplayStyle.Flex); gap(Spacing.xs); flexWrap(FlexWrap.Wrap); alignItems(AlignItems.Center) } }) {
                        Button({
                            onClick { editingAd = ad }
                            style { padding(Spacing.xs, Spacing.sm); primaryButtonStyle(colors); fontSize(Typography.xs) }
                        }) { Text("Edit") }
                        Button({
                            onClick {
                                scope.launch {
                                    try {
                                        if (ad.show) api.hideAd(ad.id) else api.unhideAd(ad.id)
                                        loadAds()
                                    } catch (_: Exception) { }
                                }
                            }
                            style {
                                padding(Spacing.xs, Spacing.sm)
                                if (!ad.show) primaryButtonStyle(colors) else secondaryButtonStyle(colors)
                                fontSize(Typography.xs)
                            }
                        }) { Text(if (ad.show) "Hide" else "Show") }
                    }
                }
            )

            DataTable(
                columns = columns,
                rows = page.data,
                total = page.total,
                offset = page.offset,
                limit = page.limit,
                onPageChange = { newOffset -> offset = newOffset }
            )
        }
    }

    if (showCreateDialog) {
        AdDialog(
            ad = null,
            api = api,
            onSaved = { loadAds() },
            onClose = { showCreateDialog = false }
        )
    }

    editingAd?.let { ad ->
        AdDialog(
            ad = ad,
            api = api,
            onSaved = { loadAds() },
            onClose = { editingAd = null }
        )
    }
}
